import posthog from 'posthog-js';
import type { CaptureResult } from 'posthog-js';
import { config } from '@/config';

const POSTHOG_KEY_PLACEHOLDER = '<POSTHOG_PROJECT_API_KEY>';
const SENSITIVE_URL_PARAMS = [
  'token',
  'code',
  'requestId',
  'sessionToken',
  'idToken',
  'access_token',
  'refresh_token',
  'oobCode',
  'callbackUrl',
];
const ABSOLUTE_URL_PATTERN = /^[a-z][a-z0-9+.-]*:\/\//i;
const MAX_SANITIZE_DEPTH = 4;
let initialized = false;

export const stripUrlQueryAndHash = (value: string): string => {
  if (!ABSOLUTE_URL_PATTERN.test(value)) {
    return value;
  }
  const cut = value.search(/[?#]/);
  return cut === -1 ? value : value.slice(0, cut);
};

const sanitizeValue = (value: unknown, depth: number): unknown => {
  if (typeof value === 'string') {
    return stripUrlQueryAndHash(value);
  }
  if (depth >= MAX_SANITIZE_DEPTH || value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item, depth + 1));
  }
  if (value instanceof Date) {
    return value;
  }
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, item]) => [
      key,
      sanitizeValue(item, depth + 1),
    ]),
  );
};

export const stripUrlQueriesBeforeSend = (event: CaptureResult | null): CaptureResult | null => {
  if (!event) {
    return event;
  }
  return {
    ...event,
    properties: sanitizeValue(event.properties, 0) as CaptureResult['properties'],
    ...(event.$set && { $set: sanitizeValue(event.$set, 0) as CaptureResult['$set'] }),
    ...(event.$set_once && {
      $set_once: sanitizeValue(event.$set_once, 0) as CaptureResult['$set_once'],
    }),
  };
};

const isPostHogEnabled = () =>
  config.posthog.enabled &&
  config.posthog.apiKey &&
  config.posthog.apiKey !== POSTHOG_KEY_PLACEHOLDER;

export const initPostHog = (): void => {
  if (initialized || !isPostHogEnabled()) {
    return;
  }

  posthog.init(config.posthog.apiKey, {
    api_host: config.posthog?.apiHost || 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    capture_dead_clicks: false,
    disable_session_recording: true,
    mask_personal_data_properties: true,
    custom_personal_data_properties: SENSITIVE_URL_PARAMS,
    before_send: stripUrlQueriesBeforeSend,
  });

  initialized = true;
};

export const capturePostHogEvent = (
  eventName: string,
  properties?: Record<string, string | number | boolean | null | undefined>,
): void => {
  if (!initialized) {
    return;
  }
  posthog.capture(eventName, properties);
};

export const identifyPostHogUser = (
  userId: string,
  properties?: Record<string, string | boolean | undefined>,
): void => {
  if (!initialized || !userId) {
    return;
  }
  posthog.identify(userId, properties);
};

export const resetPostHog = (): void => {
  if (!initialized) {
    return;
  }
  posthog.reset();
};
